import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../styles/success.css';

/* ── small SVG icons ── */
const CheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const ExternalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const STEPS = [
  {
    num: '01',
    title: 'Complete Your Setup Form',
    body: 'Tell us about your business — services, tone, channels, FAQs. Takes about 10 minutes. The more detail you give, the better your AI performs on day one.',
    cta: { label: 'Open Setup Form', to: '/setup-form', primary: true },
    status: 'Action required',
    statusColor: 'amber',
  },
  {
    num: '02',
    title: 'Book Your Onboarding Call',
    body: 'Once your form is submitted, schedule a call with our team. We\'ll walk through your configuration, confirm everything looks right, and answer any questions.',
    cta: { label: 'Book a Call', href: 'https://calendly.com/klaivox', primary: false },
    status: 'After step 1',
    statusColor: 'blue',
  },
  {
    num: '03',
    title: 'Your AI Goes Live',
    body: 'After the call, our team configures and tests your AI. We\'ll send your embed code, phone number, and full dashboard access. Live within 24 hours.',
    cta: null,
    status: 'Within 24 hours',
    statusColor: 'green',
  },
];

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [visible, setVisible] = useState(false);

  /* fade-in on mount */
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  /* Stripe passes ?session_id= — we just display it for support reference */
  const sessionId = searchParams.get('session_id');

  return (
    <main className={`sp-page ${visible ? 'sp-page--visible' : ''}`}>
      {/* background blobs */}
      <div className="sp-blob sp-blob--1" aria-hidden="true" />
      <div className="sp-blob sp-blob--2" aria-hidden="true" />

      <div className="sp-wrap">

        {/* ── HEADER CARD ── */}
        <div className="sp-header-card">
          <div className="sp-check-ring" aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
              stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <div className="sp-confirm-badge">
            <span className="sp-confirm-dot" aria-hidden="true" />
            Payment Confirmed
          </div>

          <h1 className="sp-title">You're In.</h1>
          <p className="sp-subtitle">
            Welcome to Klaivox. Your plan is confirmed and your receipt has been
            sent to your email via Stripe.
          </p>

          <div className="sp-timeline-hint">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Your AI will be live within 24 hours of your onboarding call
          </div>

          {sessionId && (
            <p className="sp-session-id">
              Order ref: <code>{sessionId.slice(0, 24)}…</code>
            </p>
          )}
        </div>

        {/* ── STEPS ── */}
        <div className="sp-steps-label">What happens next</div>

        <div className="sp-steps">
          {STEPS.map((step, i) => (
            <div key={i} className={`sp-step sp-step--${step.statusColor}`}>
              <div className="sp-step__left">
                <div className="sp-step__num">{step.num}</div>
                {i < STEPS.length - 1 && <div className="sp-step__connector" />}
              </div>
              <div className="sp-step__body">
                <div className="sp-step__top">
                  <h3 className="sp-step__title">{step.title}</h3>
                  <span className={`sp-step__badge sp-step__badge--${step.statusColor}`}>
                    {step.statusColor === 'amber' && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/></svg>
                    )}
                    {step.status}
                  </span>
                </div>
                <p className="sp-step__text">{step.body}</p>
                {step.cta && (
                  step.cta.to ? (
                    <Link
                      to={step.cta.to}
                      className={`sp-step__cta ${step.cta.primary ? 'sp-step__cta--primary' : 'sp-step__cta--ghost'}`}
                    >
                      {step.cta.label}
                      <ArrowIcon />
                    </Link>
                  ) : (
                    <a
                      href={step.cta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sp-step__cta sp-step__cta--ghost"
                    >
                      {step.cta.label}
                      <ExternalIcon />
                    </a>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ── DASHBOARD BLOCK ── */}
        <div className="sp-dash-card">
          <div className="sp-dash-card__left">
            <div className="sp-dash-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
              </svg>
            </div>
            <div>
              <div className="sp-dash-card__title">Your dashboard is ready to explore</div>
              <div className="sp-dash-card__sub">Metrics, conversations, and settings — all in one place</div>
            </div>
          </div>
          <a
            href="https://app.klaivox.com"
            target="_blank"
            rel="noopener noreferrer"
            className="sp-dash-btn"
          >
            Access Dashboard
            <ExternalIcon />
          </a>
        </div>

        {/* ── SUPPORT ── */}
        <p className="sp-support">
          Questions before your call? Reply to your welcome email or reach us at{' '}
          <a href="mailto:klaivoxai@gmail.com">klaivoxai@gmail.com</a>
        </p>

        <Link to="/" className="sp-back-link">← Back to home</Link>
      </div>
    </main>
  );
}
